import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Paper, Button, Container, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';
import config from '../config';

const FormResponsePage = () => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        setLoading(true);
        setError(null);
        setError('');

        // Log the full URL being requested
        const requestUrl = `${config.API_BASE_URL}/forms/response/${responseId}/`;
        // console.log('Fetching form data from:', requestUrl);

        // Get form data using the response ID
        const response = await axios.get(
          requestUrl,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: true
          }
        );

        // console.log('Raw API response:', JSON.parse(JSON.stringify(response.data)));

        if (response.data.error) {
          throw new Error(response.data.error);
        }

        // Get questions from the response
        const questions = Array.isArray(response.data.questions)
          ? response.data.questions
          : [];

        // console.log('Questions data:', {
        //   questionsCount: questions.length,
        //   questions: questions.map(q => ({
        //     id: q.id,
        //     text: q.text,
        //     type: q.type,
        //     required: q.required,
        //     options: q.options?.length || 0
        //   }))
        // });

        // Prepare the form data structure
        const formData = {
          form: response.data.form || {},
          questions: questions.map(question => ({
            id: question.id,
            text: question.text,
            description: question.description || '',
            type: question.type,
            required: question.required || false,
            order: question.order || 0,
            options: question.options || []
          }))
        };

        // console.log('Processed form data:', formData);
        setFormData(formData);
      } catch (err) {
        // console.error('Error fetching form data:', err);
        setError(err.response?.data?.error || 'Failed to load form. The link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    if (responseId) {
      fetchFormData();
    } else {
      setError('Invalid form link. Please check the URL and try again.');
      setLoading(false);
    }
  }, [responseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess('');

    try {
      // Validate required fields
      const requiredQuestions = [];
      formData?.questions?.forEach(question => {
        if (question.required && (formResponses[question.id] === undefined || formResponses[question.id] === '')) {
          requiredQuestions.push({
            question_id: question.id,
            message: 'This field is required'
          });
        }
      });

      if (requiredQuestions.length > 0) {
        setError({
          question_id: requiredQuestions[0].question_id,
          message: requiredQuestions[0].message
        });

        // Scroll to the first error
        const errorElement = document.getElementById(`question-${requiredQuestions[0].question_id}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        setSubmitting(false);
        return;
      }

      // Get the current user if authenticated
      const currentUser = JSON.parse(localStorage.getItem('user'));
      const userId = currentUser?.id;

      // Prepare the responses in the format expected by the backend
      const formattedResponses = Object.entries(formResponses).map(([questionId, answer]) => ({
        question: questionId,
        answer: answer
      }));

      // console.log('Submitting form responses:', formattedResponses);

      // Submit the form responses with user ID
      const response = await axios.post(
        `${config.API_BASE_URL}/forms/response/${responseId}/submit/`,
        {
          responses: formattedResponses,
          user_id: userId
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );

      // console.log('Form submitted successfully:', response.data);

      // Show success message
      setSuccess('Your response has been submitted successfully!');

      // Reset the form
      setFormResponses({});

      // Redirect back to the event page after a short delay
      setTimeout(() => {
        // Get the event ID from the form data or URL
        const eventId = formData?.event?.id || new URLSearchParams(window.location.search).get('event_id');
        if (eventId) {
          navigate(`/event/${eventId}`);
        } else {
          // Fallback to home if no event ID is found
          navigate('/');
        }
      }, 2000);

    } catch (err) {
      // console.error('Error submitting form:', err);

      // Handle specific error cases
      if (err.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        // console.error('Response data:', err.response.data);
        // console.error('Response status:', err.response.status);
        // console.error('Response headers:', err.response.headers);

        if (err.response.status === 400) {
          // Handle validation errors
          if (err.response.data.responses) {
            // Find the first error
            const firstError = err.response.data.responses[0];
            if (firstError) {
              setError({
                question_id: firstError.question,
                message: firstError.error || 'Invalid input'
              });

              // Scroll to the first error
              const errorElement = document.getElementById(`question-${firstError.question}`);
              if (errorElement) {
                errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }

              return;
            }
          }
        }
      } else if (err.request) {
        // The request was made but no response was received
        // console.error('No response received:', err.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        // console.error('Error:', err.message);
      }

      setError({
        message: err.response?.data?.error ||
          err.response?.data?.detail ||
          'Failed to submit the form. Please check your connection and try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => window.location.href = '/'}>
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Thank You!
          </Typography>
          <Typography variant="body1" paragraph>
            Your response has been submitted successfully.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => window.location.href = '/'}>
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  // Debug: Log the current form data before rendering
  // console.log('Rendering form with data:', {
  //   formTitle: formData?.form?.title,
  //   questionsCount: formData?.questions?.length || 0,
  //   firstQuestion: formData?.questions?.[0],
  //   formData: formData
  // });

  // Helper function to render question input based on type
  const renderQuestionInput = (question) => {
    const commonProps = {
      id: `question-${question.id}`,
      required: question.required,
      style: { width: '100%', padding: '8px', marginTop: '8px' },
      value: formResponses[question.id] || '',
      onChange: (e) =>
        setFormResponses({
          ...formResponses,
          [question.id]: e.target.value
        })
    };

    switch (question.type) {
      case 'text':
        return <input type="text" {...commonProps} />;

      case 'textarea':
        return <textarea {...commonProps} style={{ ...commonProps.style, minHeight: '100px' }} />;

      case 'select':
      case 'radio':
      case 'checkbox':
        return (
          <select {...commonProps}>
            <option value="">Select an option</option>
            {question.options?.map((option) => (
              <option key={option.id || option.value} value={option.value}>
                {option.text || option.value}
              </option>
            ))}
          </select>
        );

      default:
        return <input type="text" {...commonProps} />;
    }
  };

  // Display success message if form was submitted successfully
  if (success) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            {success}
          </Typography>
          <Typography color="textSecondary" paragraph>
            Thank you for your response.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
          >
            Submit Another Response
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {error && error.message && !error.question_id && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error.message}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Typography variant="h4" gutterBottom>
            {formData?.form?.title || 'Untitled Form'}
          </Typography>

          {formData?.form?.description && (
            <Typography variant="body1" color="textSecondary" paragraph>
              {formData.form.description}
            </Typography>
          )}

          {formData?.questions?.length > 0 ? (
            formData.questions.map((question) => (
              <Box
                key={question.id || `question-${Math.random().toString(36).substr(2, 9)}`}
                mb={4}
                id={`question-${question.id}`}
              >
                <Typography variant="subtitle1" gutterBottom>
                  {question.text} {question.required && <span style={{ color: 'red' }}>*</span>}
                  {question.description && (
                    <Typography variant="caption" display="block" color="textSecondary">
                      {question.description}
                    </Typography>
                  )}
                </Typography>

                {renderQuestionInput(question)}

                {/* Display any validation errors */}
                {error && error.question_id === question.id && (
                  <Typography color="error" variant="caption" display="block">
                    {error.message}
                  </Typography>
                )}
              </Box>
            ))
          ) : (
            <Box mt={2} mb={4}>
              <Typography variant="body1" color="textSecondary">
                This form doesn't have any questions yet.
              </Typography>
            </Box>
          )}

          <Box mt={4} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default FormResponsePage;
